import type { OptionBuyingStrategy } from '@einhaus/project314-types';

/**
 * Display name for an option buying strategy, falling back to a stable
 * id-based name for unnamed strategies.
 */
export const formatOptionBuyingStrategyName = (strategy: Pick<OptionBuyingStrategy, 'id' | 'name'>): string => {
    return strategy.name?.trim() ? strategy.name : `Option Strategy ${strategy.id}`;
};

/**
 * Humanizes a camelCase buying method, e.g. "atTheMoney" -> "At the money".
 */
export const formatOptionBuyingMethod = (buyingMethod: OptionBuyingStrategy['buyingMethod']): string => {
    const words = buyingMethod
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase();

    return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Days-to-expiry summary, e.g. "30 DTE (21-45 range)". Returns null when the
 * strategy has no DTE configuration.
 */
export const formatOptionBuyingStrategyDte = (
    strategy: Pick<OptionBuyingStrategy, 'targetDaysToExpiry' | 'minimumDaysToExpiry' | 'maximumDaysToExpiry'>
): string | null => {
    const parts: string[] = [];

    if (strategy.targetDaysToExpiry) {
        parts.push(`${strategy.targetDaysToExpiry} DTE`);
    }

    if (strategy.minimumDaysToExpiry && strategy.maximumDaysToExpiry) {
        parts.push(`(${strategy.minimumDaysToExpiry}-${strategy.maximumDaysToExpiry} range)`);
    }

    return parts.length > 0 ? parts.join(' ') : null;
};

/**
 * One-line description of how the strategy buys options,
 * e.g. "At the money · 30 DTE (21-45 range)".
 */
export const formatOptionBuyingStrategyMeta = (strategy: OptionBuyingStrategy): string => {
    const parts = [formatOptionBuyingMethod(strategy.buyingMethod)];
    const dte = formatOptionBuyingStrategyDte(strategy);

    if (dte) {
        parts.push(dte);
    }

    return parts.join(' · ');
};
